import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { CacheLookupPolicy, InteractionRequiredAuthError } from '@azure/msal-browser';
import { environment } from '#environments/environment';
import { AuthService } from '#services/shared';
import { MsalService } from '@azure/msal-angular';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(
    private route: Router,
    private msalService: MsalService,
    public authSvc: AuthService
  ) {}

  // getActiveAccount or getAllAccounts().First
  private ensureActiveAccount() {
    let account = this.msalService.instance.getActiveAccount();
    if (!account) {
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts?.length) {
        account = accounts[0];
        this.msalService.instance.setActiveAccount(account);
      }
    }
    return account;
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    request = this.addAuthHeader(request);
    return next.handle(request).pipe(catchError((error) => {

        if (error.status === 401) {
          // getActiveAccount or getAllAccounts().First
          const account = this.ensureActiveAccount();

          // If no account exists, redirect to login
          if (!account) {
            sessionStorage.clear();
            this.msalService.loginRedirect();
            return throwError(() => error);
          }

          // Try silent token acquisition
          const scopes = [ environment.msalConfig.auth.user_impersonation ];
          return this.msalService.acquireTokenSilent({
            scopes:scopes,
            account: account,
            forceRefresh: true,
            cacheLookupPolicy: CacheLookupPolicy.Default,
          }).pipe(
            switchMap((tokenResponse: any) => {
              const accessToken = tokenResponse.accessToken;
              sessionStorage.setItem('Token', accessToken);

              // retry same request with new access token
              const retryRequest = request.clone({
                setHeaders: { Authorization: `Bearer ${accessToken}` }
              });
              return next.handle(retryRequest);
            }),
            catchError((tokenErr) => {
              const isInteractionRequired =
                tokenErr instanceof InteractionRequiredAuthError ||
                tokenErr?.errorCode === 'interaction_required' ||
                tokenErr?.errorCode === 'login_required' ||
                tokenErr?.errorCode === 'consent_required';

              if (isInteractionRequired) {
                this.msalService.acquireTokenRedirect({
                  scopes: scopes,
                  account: account,
                } as any);
              }
              return throwError(() => tokenErr);
            })
          );
        }

        return throwError(() => error);
      }));

  }

  private addAuthHeader(request: HttpRequest<any>) {
    const authHeader = this.authSvc.getAuthorizationHeader();
    if (authHeader) {
      return request.clone({
        setHeaders: { 
          Authorization: authHeader 
        }
      });
    }
    return request;
  }
}